import type { BoardDoc } from '../doc/BoardDoc';
import { newId } from '../doc/BoardDoc';
import { rotatedBounds, unionRects, type Point, type Rect } from '../doc/geometry';
import {
  DEFAULT_KANBAN_SIZE,
  DEFAULT_LINK_SIZE,
  DEFAULT_TABLE_SIZE,
  DEFAULT_TODO_SIZE,
  DEFAULT_NOTE_SIZE,
  DEFAULT_RICHTEXT_SIZE,
  DEFAULT_SHAPE_SIZE,
  DEFAULT_TITLE_SIZE,
  NOTE_COLORS,
  type Geometry,
  type Item,
  type ItemPatch,
  type NewItem,
} from '../doc/schema';
import { api } from '../sync/api';
import type { PaneStore } from './paneStore';
import { withFrameContents } from './frames';
import { canSelect } from './ink';
import { toolSettings } from './toolSettings';
import { loadRich, richSync } from './text/richLoader';
import { hasStoredClipboard, readStoredClipboard, rememberClipboard, snapshotForClipboard, type ClipboardPayload } from './clipboard';
import { cloneAssetLocally } from './assets/localAssets';
import { enqueueUpload } from '../sync/uploadQueue';
import { RICH_TYPES } from '../doc/schema';
import { centerOn, fitRect, screenToWorld, visibleWorldRect, zoomAt, type Camera } from './viewport/camera';

export interface Ctx {
  board: BoardDoc;
  pane: PaneStore;
}

// ---- selection helpers ------------------------------------------------------

export function selectedItems({ board, pane }: Ctx): Item[] {
  const out: Item[] = [];
  for (const id of pane.getState().selection) {
    const item = board.index.get(id);
    if (item) out.push(item);
  }
  return out;
}

export function selectionBounds(ctx: Ctx): Rect | null {
  const { transient } = ctx.pane.getState();
  return unionRects(selectedItems(ctx).map((i) => rotatedBounds(withTransient(i, transient[i.id]))));
}

export function withTransient<T extends Geometry>(item: T, t: Partial<Geometry> | undefined): T {
  return t ? { ...item, ...t } : item;
}

/** Selects everything the current tool can select: strokes with the draw tool, everything else otherwise. */
export function selectAll({ board, pane }: Ctx): void {
  const { tool, select } = pane.getState();
  select(board.index.all().filter((i) => canSelect(i, tool) && !board.isHidden(i) && !board.isLocked(i)).map((i) => i.id));
}

export function deleteSelection({ board, pane }: Ctx): void {
  const { selection, clearSelection } = pane.getState();
  if (!selection.size) return;
  const ids = [...selection].filter((id) => {
    const item = board.index.get(id);
    return item && !board.isLocked(item);
  });
  if (ids.length) board.deleteItems(ids);
  clearSelection();
}

/** Writes the in-progress transient geometry to the document in one undoable transaction. */
export function commitTransient({ board, pane }: Ctx): void {
  const { transient, clearTransient } = pane.getState();
  const patches: [string, ItemPatch][] = [];
  for (const [id, g] of Object.entries(transient)) {
    const patch: ItemPatch = {};
    if (g.x !== undefined) patch.x = round(g.x);
    if (g.y !== undefined) patch.y = round(g.y);
    if (g.w !== undefined) patch.w = round(g.w);
    if (g.h !== undefined) patch.h = round(g.h);
    if (g.rotation !== undefined) patch.rotation = g.rotation;
    patches.push([id, patch]);
  }
  if (patches.length) board.updateItems(patches);
  clearTransient();
}

const round = (v: number) => Math.round(v * 100) / 100;

export function nudgeSelection(ctx: Ctx, dx: number, dy: number): void {
  const items = withFrameContents(ctx.board, selectedItems(ctx).filter((i) => !ctx.board.isLocked(i)));
  const patches: [string, ItemPatch][] = items.map((item) => [item.id, { x: item.x + dx, y: item.y + dy }]);
  if (patches.length) ctx.board.updateItems(patches);
}

// ---- creating items ---------------------------------------------------------

export function createNoteAt(ctx: Ctx, world: Point, edit = true, text = ''): string {
  const size = DEFAULT_NOTE_SIZE;
  const id = ctx.board.createItem({
    type: 'note',
    x: world.x - size / 2,
    y: world.y - size / 2,
    w: size,
    h: size,
    props: { text, color: NOTE_COLORS[0], fontSize: 20 },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createTitleAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_TITLE_SIZE;
  const id = ctx.board.createItem({
    type: 'title',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { text: '', fontSize: 40, bold: true, align: 'left' },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createRichTextAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_RICHTEXT_SIZE;
  const id = ctx.board.createItem({
    type: 'richtext',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 16 },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export async function createTableAt(ctx: Ctx, world: Point, edit = true): Promise<string> {
  await loadRich();
  const { w, h } = DEFAULT_TABLE_SIZE;
  const id = ctx.board.createItem({
    type: 'table',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 14, contentJson: (await loadRich()).tableJson(3, 3) },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export async function createTodoAt(ctx: Ctx, world: Point, edit = true): Promise<string> {
  await loadRich();
  const { w, h } = DEFAULT_TODO_SIZE;
  const id = ctx.board.createItem({
    type: 'todo',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 15, contentJson: (await loadRich()).taskListJson(['']) },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createKanbanAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_KANBAN_SIZE;
  const id = ctx.board.createItem({
    type: 'kanban',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: {
      columns: [
        { id: newId(), title: 'To do', cards: [] },
        { id: newId(), title: 'Doing', cards: [] },
        { id: newId(), title: 'Done', cards: [] },
      ],
    },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

/** Creates a shape from the current tool settings, either in a dragged rect or centred on a point. */
export function createShapeAt(ctx: Ctx, where: Rect | Point, opts: { flip?: boolean } = {}): string {
  const t = toolSettings.getState();
  const rect: Rect =
    'w' in where
      ? where
      : {
          x: where.x - DEFAULT_SHAPE_SIZE / 2,
          y: where.y - DEFAULT_SHAPE_SIZE / 2,
          w: DEFAULT_SHAPE_SIZE,
          h: DEFAULT_SHAPE_SIZE,
        };
  const isLine = t.shapeKind === 'line' || t.shapeKind === 'arrow';
  const id = ctx.board.createItem({
    type: 'shape',
    x: round(rect.x),
    y: round(rect.y),
    w: round(Math.max(1, rect.w)),
    h: round(Math.max(isLine ? 0 : 1, rect.h)),
    props: {
      kind: t.shapeKind,
      fill: isLine ? 'none' : t.fill,
      stroke: t.stroke === 'none' && (isLine || t.fill === 'none') ? '#111111' : t.stroke,
      strokeWidth: t.strokeWidth,
      flip: opts.flip ?? false,
    },
  });
  ctx.pane.getState().select([id]);
  return id;
}

const DEFAULT_FRAME_W = 800;
const DEFAULT_FRAME_H = 600;

/** A frame at a point (default size, centred) or filling a dragged rect. Frames start at the bottom. */
export function createFrameAt(ctx: Ctx, where: Rect | Point): string {
  const rect: Rect =
    'w' in where
      ? where
      : { x: where.x - DEFAULT_FRAME_W / 2, y: where.y - DEFAULT_FRAME_H / 2, w: DEFAULT_FRAME_W, h: DEFAULT_FRAME_H };
  const count = ctx.board.index.all().filter((i) => i.type === 'frame').length;
  const id = ctx.board.createItem({
    type: 'frame',
    x: round(rect.x),
    y: round(rect.y),
    w: round(Math.max(40, rect.w)),
    h: round(Math.max(40, rect.h)),
    props: { title: `Frame ${count + 1}`, color: null },
  });
  ctx.pane.getState().select([id]);
  return id;
}

const URL_RE = /^https?:\/\/[^\s]+$/i;
export const looksLikeUrl = (text: string): boolean => URL_RE.test(text.trim());

/** Creates a link card and fills it in from the server's preview endpoint. */
export function createLinkAt(ctx: Ctx, url: string, world: Point): string {
  const { w, h } = DEFAULT_LINK_SIZE;
  url = url.trim();
  const id = ctx.board.createItem({
    type: 'link',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { url, title: '', description: '', image: null, site: '', status: 'loading' },
  });
  ctx.pane.getState().select([id]);
  void refreshLinkPreview(ctx.board, id);
  return id;
}

interface Preview {
  url: string;
  title: string;
  description: string;
  image: string | null;
  site: string;
}

export async function refreshLinkPreview(board: BoardDoc, id: string): Promise<void> {
  const item = board.index.get(id);
  if (!item || item.type !== 'link') return;
  try {
    const p = await api.post<Preview>('/link-preview', { url: item.props.url, boardId: board.remote ? board.boardId : undefined });
    if (!board.index.has(id)) return;
    board.updateItem(id, {
      props: { title: p.title, description: p.description, image: p.image, site: p.site, status: 'ok' },
    });
  } catch {
    if (board.index.has(id)) board.updateItem(id, { props: { status: 'error' } });
  }
}

/** World point at the centre of the pane. */
export function viewCenter(pane: PaneStore): Point {
  const { camera, size } = pane.getState();
  return screenToWorld(camera, { x: size.w / 2, y: size.h / 2 });
}

// ---- clipboard --------------------------------------------------------------

// Storage lives in clipboard.ts (this tab, other tabs via localStorage, the system clipboard via
// the copy/cut events wired in useKeyboard). Repeated pastes of one copy step down and right.
let pasteCount = 0;
let pastedStamp = '';

const hasRich = (items: Item[]) => items.some((i) => RICH_TYPES.has(i.type));

/** Rewrites image/file props to freshly cloned assets when items cross into another board. */
async function relinkAssets(items: NewItem[], fromBoard: string, to: BoardDoc): Promise<void> {
  if (fromBoard === to.boardId) return;
  for (const item of items) {
    if (item.type === 'boardcard') {
      // a custom cover belongs to the source board; without one the card shows the live preview
      if (!item.props.coverAssetId) continue;
      const newId = await cloneAssetLocally(item.props.coverAssetId);
      item.props = { ...item.props, coverAssetId: newId };
      if (newId && to.remote) void enqueueUpload(to.boardId, newId);
      continue;
    }
    if (item.type !== 'image' && item.type !== 'file') continue;
    const newId = await cloneAssetLocally(item.props.assetId);
    if (!newId) continue;
    item.props = { ...item.props, assetId: newId } as typeof item.props;
    if (to.remote) void enqueueUpload(to.boardId, newId);
  }
}

/**
 * Copies the selection. The snapshot is taken at once, so the copy event that follows Ctrl+C
 * can fill the system clipboard synchronously; rich bodies whose module is not loaded yet are
 * snapshotted again under the same stamp once it is, and that fuller copy then wins on paste.
 */
export async function copySelection(ctx: Ctx): Promise<ClipboardPayload | null> {
  const items = selectedItems(ctx);
  if (!items.length) return null;
  const ids = items.map((i) => i.id);
  let payload = snapshotForClipboard(ctx.board, ids);
  rememberClipboard(payload);
  if (hasRich(items) && !richSync()) {
    await loadRich();
    if (ids.every((id) => ctx.board.index.has(id))) {
      payload = snapshotForClipboard(ctx.board, ids, payload.stamp);
      rememberClipboard(payload);
    }
  }
  return payload;
}

export async function cutSelection(ctx: Ctx): Promise<void> {
  await copySelection(ctx);
  deleteSelection(ctx);
}

/**
 * Pastes the given copy (from a paste event) or the newest one this browser holds. Copies from
 * another board land centred in the view unless a point is given, with their assets cloned;
 * copies from this board step 24 px down and right of the originals on every paste. Pasted
 * items form groups of their own, and keep their layer only where the target board has it.
 */
export async function pasteClipboard(ctx: Ctx, at?: Point, payload?: ClipboardPayload | null): Promise<void> {
  const clip = payload ?? readStoredClipboard();
  if (!clip || !clip.items.length) return;
  if (hasRich(clip.items)) await loadRich();
  if (clip.stamp !== pastedStamp) {
    pastedStamp = clip.stamp;
    pasteCount = 0;
  }
  pasteCount++;
  const sameBoard = clip.boardId === ctx.board.boardId;
  if (!sameBoard && !at) at = viewCenter(ctx.pane);
  const offset = at ? null : 24 * pasteCount;
  const bounds = unionRects(clip.items.map(rotatedBounds));
  const groups = new Map<string, string>();
  const inputs: NewItem[] = clip.items.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    let x = item.x;
    let y = item.y;
    if (offset !== null) {
      x += offset;
      y += offset;
    } else if (at && bounds) {
      x = at.x - bounds.w / 2 + (item.x - bounds.x);
      y = at.y - bounds.h / 2 + (item.y - bounds.y);
    }
    let groupId: string | null = null;
    if (item.groupId) {
      groupId = groups.get(item.groupId) ?? newId();
      groups.set(item.groupId, groupId);
    }
    const layerId = sameBoard || (item.layerId && ctx.board.layer(item.layerId)) ? item.layerId : null;
    return { ...rest, x, y, groupId, layerId, id: newId() } as NewItem;
  });
  await relinkAssets(inputs, clip.boardId, ctx.board);
  const ids = ctx.board.createItems(inputs);
  ctx.pane.getState().select(ids);
}

export async function duplicateSelection(ctx: Ctx): Promise<void> {
  const items = selectedItems(ctx);
  if (!items.length) return;
  if (hasRich(items)) await loadRich();
  const snapshot = ctx.board.snapshotItems(items.map((i) => i.id));
  const inputs: NewItem[] = snapshot.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    return { ...rest, x: item.x + 24, y: item.y + 24 } as NewItem;
  });
  ctx.pane.getState().select(ctx.board.createItems(inputs));
}

export function hasClipboard(): boolean {
  return hasStoredClipboard();
}

// ---- camera -----------------------------------------------------------------

export function zoomBy(pane: PaneStore, factor: number, screenPt?: Point): void {
  const { camera, size, setCamera } = pane.getState();
  const pt = screenPt ?? { x: size.w / 2, y: size.h / 2 };
  setCamera(zoomAt(camera, pt, camera.zoom * factor));
}

export function zoomTo(pane: PaneStore, zoom: number): void {
  const { camera, size, setCamera } = pane.getState();
  setCamera(zoomAt(camera, { x: size.w / 2, y: size.h / 2 }, zoom));
}

export function zoomToFit(ctx: Ctx): void {
  const all = ctx.board.index.all();
  const bounds = unionRects(all.map(rotatedBounds));
  const { size, setCamera } = ctx.pane.getState();
  if (!bounds) {
    setCamera(centerOn({ x: 0, y: 0 }, size, 1));
    return;
  }
  setCamera(fitRect(bounds, size, 64, 4));
}

export function zoomToSelection(ctx: Ctx): void {
  const bounds = selectionBounds(ctx);
  if (!bounds) return zoomToFit(ctx);
  const { size, setCamera } = ctx.pane.getState();
  setCamera(fitRect(bounds, size, 96, 4));
}

export function visibleRect(pane: PaneStore): Rect {
  const { camera, size } = pane.getState();
  return visibleWorldRect(camera, size);
}

export function cameraFor(pane: PaneStore): Camera {
  return pane.getState().camera;
}

// ---- cross-pane / cross-board ----------------------------------------------------

/** Copies items into another board at new positions (split view drag between different boards). */
export async function copyItemsToBoard(
  from: BoardDoc,
  to: BoardDoc,
  positions: Map<string, Point>,
): Promise<string[]> {
  const ids = [...positions.keys()];
  const source = ids.map((id) => from.index.get(id)).filter((i): i is Item => !!i);
  if (hasRich(source)) await loadRich();
  const snapshot = from.snapshotItems(ids);
  const inputs: NewItem[] = snapshot.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, groupId: _g, layerId: _l, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    void _g;
    void _l;
    const p = positions.get(item.id) ?? { x: item.x, y: item.y };
    return { ...rest, x: p.x, y: p.y } as NewItem;
  });
  await relinkAssets(inputs, from.boardId, to);
  return to.createItems(inputs);
}

// ---- dev helpers ------------------------------------------------------------

export function stressTest(ctx: Ctx, count: number): void {
  const center = viewCenter(ctx.pane);
  const spread = Math.sqrt(count) * 260;
  const inputs: NewItem[] = [];
  for (let i = 0; i < count; i++) {
    inputs.push({
      type: 'note',
      x: center.x + (Math.random() - 0.5) * spread,
      y: center.y + (Math.random() - 0.5) * spread,
      w: 160 + Math.random() * 120,
      h: 160 + Math.random() * 120,
      rotation: Math.round((Math.random() - 0.5) * 20),
      props: {
        text: `Note ${i + 1}`,
        color: NOTE_COLORS[i % NOTE_COLORS.length]!,
        fontSize: 20,
      },
    });
  }
  ctx.board.createItems(inputs);
}

// ---- export -----------------------------------------------------------------

/** Downloads the board's items as JSON (assets referenced by id; the files come with a later export). */
// Board backups (JSON, ZIP with images) live in export/backup.ts and run from the export panel.
